import { Injectable, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { filter, map, merge, NEVER, of, Subject, switchMap, take, tap } from 'rxjs';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AccountSelectors, UserActions } from '@core/account-data';
import { AdminsSelectDialogComponent, AdminsSelectDialogResult } from '@core/account-smart-ui';
import type { User } from '@core/account-types';
import { AnalyticsMode } from '@core/analytics-types';
import { CandidateBulkActions, FullCandidateActions } from '@core/candidate-data';
import type { FullCandidate } from '@core/candidate-types';
import {
  getCommonOfferPlacements,
  ShareCandidatesAnalytics,
  ShareCandidatesAnalyticsTrigger,
} from '@core/candidate-util';
import { filterBoolean, filterDefined } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { offerIdsFromPlacements, offerSelectOptionsFromPlacements } from '@core/placements-data';
import { PlacementActionsStrategyService } from '@core/placements-legacy';
import type { Placement } from '@core/placements-types';
import { DialogService, DialogWidth, NotificationsController } from '@core/user-interface-legacy';
import { uniqByKey } from '@core/utils-legacy';

import type { CandidateSharingConfirmDialogResult } from '../components/candidate-sharing-confirm-dialog/candidate-sharing-confirm-dialog.component';
import { CandidateSharingConfirmDialogComponent } from '../components/candidate-sharing-confirm-dialog/candidate-sharing-confirm-dialog.component';
import { CandidateSharingOfferSelectDialogComponent } from '../components/candidate-sharing-offer-select-dialog/candidate-sharing-offer-select-dialog.component';

type Step = 1 | 2;

type NextStep = {
  step: Step;
  data: {
    candidates?: FullCandidate[];
    assignments?: User[];
    message?: Record<string, unknown>;
    allowedUsers?: User[];
    disallowedUsers?: User[];
  };
} | null;

type StepCache = {
  offerId: number;
  isBulk: boolean;
  1: {
    candidates: FullCandidate[];
    assignments: User[];
    allowedUsers?: User[];
    disallowedUsers?: User[];
  };
  2: { message: Record<string, unknown> };
} | null;

@Injectable({ providedIn: 'root' })
export class ShareWithTeamStepService {
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);
  private readonly placementActionsStrategyService = inject(PlacementActionsStrategyService);
  private readonly actions$ = inject(Actions);
  private readonly notify = inject(NotificationsController);
  private readonly shareCandidatesAnalytics = inject(ShareCandidatesAnalytics);

  private companyName: string = '';
  private canManageTeamMembers = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ManageRecruiters),
  );
  private step$ = new Subject<NextStep>();
  private stepCache: StepCache = null;

  private singleShareSuccess$ = this.actions$.pipe(
    ofType(FullCandidateActions.shareWithTeamSuccess),
    tap(() =>
      this.notify.success(
        this.i18n.translate('app.candidate.admin_select.share_with_team.success_toast', {
          count: this.stepCache![1].assignments.length ?? 0,
        }),
      ),
    ),
    tap(() =>
      this.shareCandidatesAnalytics.trackAddTeamMembers({
        mode: AnalyticsMode.Single,
        teamMembers: this.stepCache![1].assignments.length ?? 0,
        trigger: ShareCandidatesAnalyticsTrigger.Share,
        candidates: this.stepCache![1].candidates.length,
      }),
    ),
  );

  private bulkShareSuccess$ = this.actions$.pipe(
    ofType(CandidateBulkActions.shareWithTeamSuccess),
    tap(() =>
      this.shareCandidatesAnalytics.trackAddTeamMembers({
        mode: AnalyticsMode.Bulk,
        teamMembers: this.stepCache![1].assignments.length ?? 0,
        trigger: ShareCandidatesAnalyticsTrigger.Share,
        candidates: this.stepCache![1].candidates.length,
      }),
    ),
    tap(() => this.store.dispatch(UserActions.fetchCollection())),
  );

  private shareFailed$ = this.actions$.pipe(
    ofType(FullCandidateActions.shareWithTeamFailed),
    tap(() => this.notify.error(this.i18n.translate('app.common.error_something_went_wrong'))),
  );

  private nextStep$ = this.step$.pipe(
    switchMap(next => {
      switch (next?.step) {
        case 1:
          return this.openAdminListDialog({
            candidates: next.data.candidates!,
            assignments: next.data.assignments,
            allowedUsers: next.data.allowedUsers,
            disallowedUsers: next.data.disallowedUsers,
          });
        case 2:
          return this.openConfirmShareDialog({
            candidates: next.data.candidates!,
            assignments: next.data.assignments!,
            message: next.data.message,
          });
        default:
          return NEVER;
      }
    }),
  );

  constructor() {
    this.store
      .select(AccountSelectors.currentCompanyName)
      .pipe(filterBoolean)
      .subscribe((name: string) => (this.companyName = name));

    this.nextStep$.subscribe();
  }

  public open(
    isBulk: boolean,
    candidates: FullCandidate[],
    allowedUsers?: User[],
    disallowedUsers?: User[],
  ): Observable<boolean> {
    this.clearStepCache();
    this.openOfferSelectDialog(getCommonOfferPlacements(candidates))
      .pipe(filterDefined)
      .subscribe(offerId => {
        this.patchStepCache('offerId', offerId);
        this.patchStepCache('isBulk', isBulk);
        this.step$.next({
          step: 1,
          data: { candidates, allowedUsers, disallowedUsers },
        });
      });
    return merge(
      this.singleShareSuccess$,
      this.bulkShareSuccess$,
      this.shareFailed$,
      this.step$.asObservable().pipe(filter(step => step === null)),
    ).pipe(
      map(
        actionOrCancelStep =>
          (<Action<string>>actionOrCancelStep)?.type ===
            FullCandidateActions.shareWithTeamSuccess.type ||
          (<Action<string>>actionOrCancelStep)?.type ===
            CandidateBulkActions.shareWithTeamSuccess.type,
      ),
      take(1),
    );
  }

  private openOfferSelectDialog(placements: Placement[]): Observable<number | null> {
    const isBulk = new Set(placements.map(placement => placement.candidateId)).size > 1;
    const availablePlacements = this.filterAvailablePlacements(placements);
    const offerIds = offerIdsFromPlacements(availablePlacements);
    const selectOptions = uniqByKey(offerSelectOptionsFromPlacements(availablePlacements), 'id');

    if (selectOptions.length === 0) {
      return of(null);
    } else if (selectOptions.length === 1) {
      return of(offerIds[0]);
    }

    return this.dialogService.openCustom(
      CandidateSharingOfferSelectDialogComponent,
      {
        width: DialogWidth.Small,
      },
      {
        offerIds,
        selectOptions,
        isBulk,
      },
    ).afterClosed;
  }

  private filterAvailablePlacements(placements: Placement[]): Placement[] {
    return (
      placements?.filter(placement =>
        this.placementActionsStrategyService.getAvailable(placement).includes('go_to_offer'),
      ) ?? []
    );
  }

  private openAdminListDialog({
    candidates,
    assignments,
    allowedUsers,
    disallowedUsers,
  }: {
    candidates: FullCandidate[];
    assignments?: User[];
    allowedUsers?: User[];
    disallowedUsers?: User[];
  }) {
    return this.dialogService
      .openCustom(
        AdminsSelectDialogComponent,
        {
          width: DialogWidth.SmallX,
        },
        {
          assignments: assignments?.map(user => user.id) ?? [],
          heading:
            candidates.length > 1
              ? this.i18n.translate(
                  'app.candidate.admin_select.share_with_team.admin-select_dialog.heading_multiple',
                  { count: candidates.length },
                )
              : this.i18n.translate(
                  'app.candidate.admin_select.share_with_team.admin-select_dialog.heading',
                  { candidateName: candidates[0].name },
                ),
          subheading: this.i18n.translate(
            'app.candidate.admin_select.share_with_team.admin-select_dialog.subheading',
            { companyName: this.companyName },
          ),
          inviteModeEnabled: true,
          withInviteTeamMembersButton: this.canManageTeamMembers() || false,
          minSelectedAdmins: 1,
          allowedUsers,
          disallowedUsers,
        },
      )
      .afterClosed.pipe(
        tap(result => (result === null || result === undefined ? this.cancelStep() : null)),
        filterDefined,
        tap(({ users }: AdminsSelectDialogResult) => {
          this.patchStepCache(1, {
            candidates,
            assignments: users,
            allowedUsers,
            disallowedUsers,
          });
          this.nextStep(2, {
            assignments: users,
            message: this.stepCache![2]?.message,
            candidates,
          });
        }),
      );
  }

  private openConfirmShareDialog({
    candidates,
    assignments,
    message,
  }: {
    candidates: FullCandidate[];
    assignments: User[];
    message?: Record<string, unknown>;
  }) {
    return this.dialogService
      .openCustom(
        CandidateSharingConfirmDialogComponent,
        { width: DialogWidth.SmallX },
        {
          assignments,
          message,
          candidates,
          offerId: this.stepCache!.offerId,
          isBulk: this.stepCache!.isBulk,
        },
      )
      .afterClosed.pipe(
        tap(result => (result === null || result === undefined ? this.cancelStep() : null)),
        filterDefined,
        tap((result: CandidateSharingConfirmDialogResult) => {
          if (result.goBack) {
            this.patchStepCache(2, { message: result.message });
            this.nextStep(1, this.stepCache![1]);
          }
        }),
      );
  }

  private clearStepCache(): void {
    this.stepCache = null;
  }

  private patchStepCache<T extends keyof NonNullable<StepCache>>(
    prop: T,
    value: NonNullable<StepCache>[T],
  ): void {
    this.stepCache = { ...this.stepCache!, [prop]: value };
  }

  private cancelStep(): void {
    this.step$.next(null);
  }

  private nextStep(step: Step, data: NonNullable<NextStep>['data']): void {
    this.step$.next({ step, data });
  }
}
